import React, { useState, useEffect } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { Button, Card, CardBody, FormGroup, Row, Col } from "reactstrap";
import { ScoreBlockRuleGroup } from "./ScoreRule/ScoreBlockRule";

const ClickableTag = ({ data, type, onRemove, onConfigure, isConfigured }) => {
  return (
    <div
      onClick={() => onConfigure(data, type)}
      style={{
        cursor: "pointer",
        padding: "2px 0 2px 8px",
        backgroundColor: isConfigured ? "#e2fee2" : "#e1e1e1",
        display: "inline-flex",
        alignItems: "center",
        margin: "4px",
        fontSize: "12px",
        borderRadius: "4px",
        border: isConfigured ? "1px solid green" : "none",
      }}
    >
      {data.label}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(data);
        }}
        style={{
          marginLeft: "6px",
          border: "none",
          background: "transparent",
          cursor: "pointer",
          color: "#888",
          fontSize: "14px",
          transition: "color 0.2s",
        }}
        onMouseEnter={(e) => (e.target.style.color = "#333")}
        onMouseLeave={(e) => (e.target.style.color = "#888")}
      >
        ×
      </button>
    </div>
  );
};

const ConfigurationModal = ({
  isOpen,
  onClose,
  onSave,
  item,
  type,
  configuredRules,
  setConfiguredRules,
  currentBlockId,
}) => {
  const handleSave = () => {
    onSave(item, type, { rules: configuredRules });
    onClose();
  };

  if (!isOpen) return null;

  const getModalTitle = () => {
    switch (type) {
      case "prompt":
        return `Configurar Prompt - ${item?.label}`;
      case "event":
        return `Configurar Evento - ${item?.label}`;
      case "library":
        return `Configurar Biblioteca - ${item?.label}`;
      default:
        return "Configurar Item";
    }
  };

  const getItemTypeForScoreRule = () => {
    switch (type) {
      case "prompt":
        return "prompt";
      case "event":
        return "evaluable-event";
      case "library":
        return "library";
      default:
        return type;
    }
  };

  const prepareItemForScoreRule = () => {
    return [
      {
        value: item.value,
        label: item.label,
        content:
          item.content || (type === "library" && item.terms ? item.terms : ""),
        terms: item.terms || [],
      },
    ];
  };

  const getBlockId = () => {
    return currentBlockId || "new";
  };

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{getModalTitle()}</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div
            className="modal-body"
            style={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            <div className="mb-4">
              <h6 className="mb-3">
                <i className="ni ni-bullet-list-67"></i> Regras de Score do
                Bloco
              </h6>
              <div className="alert alert-info">
                <i className="ni ni-chat-round"></i> Configure as regras
                específicas para este item no bloco.{" "}
                <strong>Todas as regras do bloco devem ser atendidas</strong>{" "}
                para que a pontuação do bloco seja aplicada.
              </div>

              <ScoreBlockRuleGroup
                promptsOrEvents={prepareItemForScoreRule()}
                rules={configuredRules}
                type={getItemTypeForScoreRule()}
                configuredRules={configuredRules}
                setConfiguredRules={setConfiguredRules}
                blockId={getBlockId()}
              />
            </div>

            {Object.keys(configuredRules).length > 0 && (
              <div className="mt-4">
                <div className="alert alert-success">
                  <h6>
                    <i className="ni ni-check-bold"></i> Resumo das
                    Configurações do Bloco
                  </h6>
                  <div className="mt-2">
                    <strong>Regras configuradas:</strong>{" "}
                    {Object.keys(configuredRules).length > 0 ? "Sim" : "Não"}
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">
                      Items com regras:{" "}
                      {Object.keys(configuredRules).join(", ")}
                    </small>
                  </div>
                  <div className="mt-2">
                    <small className="text-warning">
                      <i className="ni ni-notification-70"></i> Lembre-se: Para
                      o bloco pontuar, <strong>TODAS</strong> as regras
                      configuradas devem ser atendidas.
                    </small>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <Button color="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button color="primary" onClick={handleSave}>
              Salvar Configuração do Bloco
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const BlockConfiguration = ({
  availablePrompts,
  evaluableEvents,
  libraries,
  configuredRules,
  setConfiguredRules,
  createdBlocks: externalCreatedBlocks,
  setCreatedBlocks: externalSetCreatedBlocks,
  setTotalScore,
}) => {
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [selectedBlockPrompts, setSelectedBlockPrompts] = useState([]);
  const [selectedBlockEvents, setSelectedBlockEvents] = useState([]);
  const [selectedBlockLibraries, setSelectedBlockLibraries] = useState([]);
  const [blockTitle, setBlockTitle] = useState("");
  const [blockScore, setBlockScore] = useState("");
  const [blockColor, setBlockColor] = useState("#4A90E2");
  const [blockPriority, setBlockPriority] = useState(false);
  const [createdBlocks, setCreatedBlocks] = useState(
    externalCreatedBlocks || [],
  );
  const [editingBlockIndex, setEditingBlockIndex] = useState(null);

  const [itemConfigurations, setItemConfigurations] = useState({});
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [currentConfigItem, setCurrentConfigItem] = useState(null);
  const [currentConfigType, setCurrentConfigType] = useState(null);

  useEffect(() => {
    if (setTotalScore && createdBlocks.length > 0) {
      const totalBlockScore = createdBlocks.reduce((total, block) => {
        return total + (Number(block.score) || 0);
      }, 0);
      setTotalScore(totalBlockScore);
    } else if (setTotalScore && createdBlocks.length === 0) {
      setTotalScore(0);
    }
  }, [createdBlocks, setTotalScore]);

  useEffect(() => {
    if (externalCreatedBlocks) {
      setCreatedBlocks(externalCreatedBlocks);
    }
  }, [externalCreatedBlocks]);

  useEffect(() => {
    if (externalSetCreatedBlocks) {
      externalSetCreatedBlocks(createdBlocks);
    }
  }, [createdBlocks, externalSetCreatedBlocks]);

  const generateUniqueBlockId = () => {
    return `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const [currentBlockId, setCurrentBlockId] = useState(null);

  const getCurrentBlockId = () => {
    if (editingBlockIndex !== null) {
      return createdBlocks[editingBlockIndex]?.uniqueId || editingBlockIndex;
    }
    return currentBlockId || generateUniqueBlockId();
  };

  const colorPalette = [
    "#4A90E2",
    "#50C878",
    "#FF6B6B",
    "#FFD93D",
    "#9B59B6",
    "#FF8C42",
    "#1ABC9C",
    "#E74C3C",
    "#F39C12",
    "#8E44AD",
    "#3498DB",
    "#2ECC71",
  ];

  const getItemKey = (item, type, blockId = null) => {
    const currentBlockId =
      blockId || (editingBlockIndex !== null ? editingBlockIndex : "new");
    return `${currentBlockId}_${type}_${item.value}`;
  };

  const isItemConfigured = (item, type) => {
    const blockId = getCurrentBlockId();

    const blockRules = configuredRules[blockId];
    if (blockRules && blockRules[item.value]) {
      const itemRules = blockRules[item.value];

      const hasValidRules =
        Array.isArray(itemRules) &&
        itemRules.some((rule) => {
          if (rule.conditional) {
            return false;
          }

          return (
            rule.key &&
            rule.condition &&
            rule.value !== undefined &&
            rule.value !== null &&
            rule.value !== ""
          );
        });

      if (hasValidRules) {
        return true;
      }
    }

    const key = getItemKey(item, type);
    const config = itemConfigurations[key];

    if (!config || !config.rules) {
      return false;
    }

    const hasValidItemRules = Object.values(config.rules).some((rules) => {
      if (!Array.isArray(rules)) return false;

      return rules.some((rule) => {
        if (rule.conditional) return false;

        return (
          rule.key &&
          rule.condition &&
          rule.value !== undefined &&
          rule.value !== null &&
          rule.value !== ""
        );
      });
    });

    return hasValidItemRules;
  };

  const areAllItemsConfigured = () => {
    const totalSelectedItems = getTotalSelectedItems();

    if (totalSelectedItems === 0) {
      return false;
    }

    const allPromptsConfigured =
      selectedBlockPrompts.length === 0 ||
      selectedBlockPrompts.every((prompt) =>
        isItemConfigured(prompt, "prompt"),
      );

    const allEventsConfigured =
      selectedBlockEvents.length === 0 ||
      selectedBlockEvents.every((event) => isItemConfigured(event, "event"));

    const allLibrariesConfigured =
      selectedBlockLibraries.length === 0 ||
      selectedBlockLibraries.every((library) =>
        isItemConfigured(library, "library"),
      );

    return (
      allPromptsConfigured && allEventsConfigured && allLibrariesConfigured
    );
  };

  const getUnconfiguredItemsDetails = () => {
    const unconfiguredPrompts = selectedBlockPrompts.filter(
      (p) => !isItemConfigured(p, "prompt"),
    );
    const unconfiguredEvents = selectedBlockEvents.filter(
      (e) => !isItemConfigured(e, "event"),
    );
    const unconfiguredLibraries = selectedBlockLibraries.filter(
      (l) => !isItemConfigured(l, "library"),
    );

    return {
      prompts: unconfiguredPrompts,
      events: unconfiguredEvents,
      libraries: unconfiguredLibraries,
      total:
        unconfiguredPrompts.length +
        unconfiguredEvents.length +
        unconfiguredLibraries.length,
    };
  };

  const getValidationMessage = () => {
    const totalItems = getTotalSelectedItems();

    if (totalItems < 1) {
      return `Selecione pelo menos 1 item (${totalItems}/1 mín.)`;
    }

    if (!areAllItemsConfigured()) {
      const unconfiguredPrompts = selectedBlockPrompts.filter(
        (p) => !isItemConfigured(p, "prompt"),
      ).length;
      const unconfiguredEvents = selectedBlockEvents.filter(
        (e) => !isItemConfigured(e, "event"),
      ).length;
      const unconfiguredLibraries = selectedBlockLibraries.filter(
        (l) => !isItemConfigured(l, "library"),
      ).length;
      const totalUnconfigured =
        unconfiguredPrompts + unconfiguredEvents + unconfiguredLibraries;

      return `Configure todos os itens (${totalUnconfigured} pendente${totalUnconfigured > 1 ? "s" : ""})`;
    }

    return editingBlockIndex !== null ? "Atualizar Bloco" : "Adicionar Bloco";
  };

  const handleConfigureItem = (item, type) => {
    const key = getItemKey(item, type);
    const existingConfig = itemConfigurations[key];

    let itemWithCompleteInfo = { ...item };

    if (type === "prompt") {
      const fullPromptData = availablePrompts.find(
        (p) => p.value === item.value,
      );
      if (fullPromptData) {
        itemWithCompleteInfo = {
          ...item,
          content: fullPromptData.content,
          label: fullPromptData.label,
        };
      }
    } else if (type === "event") {
      const fullEventData = evaluableEvents.find((e) => e.value === item.value);
      if (fullEventData) {
        itemWithCompleteInfo = {
          ...item,
          content: fullEventData.content,
          label: fullEventData.label,
        };
      }
    } else if (type === "library") {
      const fullLibraryData = libraries.find((l) => l.id === item.value);
      if (fullLibraryData) {
        itemWithCompleteInfo = {
          ...item,
          terms: fullLibraryData.terms,
          label: fullLibraryData.title,
        };
      }
    }

    const itemWithConfig = {
      ...itemWithCompleteInfo,
      rules: existingConfig?.rules || {},
    };

    setCurrentConfigItem(itemWithConfig);
    setCurrentConfigType(type);
    setShowConfigModal(true);
  };

  const handleRemoveItem = (item, type) => {
    switch (type) {
      case "prompt":
        setSelectedBlockPrompts((prev) =>
          prev.filter((p) => p.value !== item.value),
        );
        break;
      case "event":
        setSelectedBlockEvents((prev) =>
          prev.filter((e) => e.value !== item.value),
        );
        break;
      case "library":
        setSelectedBlockLibraries((prev) =>
          prev.filter((l) => l.value !== item.value),
        );
        break;
    }

    const key = getItemKey(item, type);
    setItemConfigurations((prev) => {
      const newConfig = { ...prev };
      delete newConfig[key];
      return newConfig;
    });
  };

  const getTotalSelectedItems = () => {
    return (
      (selectedBlockPrompts?.length || 0) +
      (selectedBlockEvents?.length || 0) +
      (selectedBlockLibraries?.length || 0)
    );
  };

  const saveItemConfiguration = (item, type, configData) => {
    const key = getItemKey(item, type);

    setItemConfigurations((prev) => ({
      ...prev,
      [key]: configData,
    }));

    setSelectedBlockPrompts((prev) => [...prev]);
    setSelectedBlockEvents((prev) => [...prev]);
    setSelectedBlockLibraries((prev) => [...prev]);

    setCreatedBlocks((prev) =>
      prev.map((block) => {
        if (block.prompts.find((p) => p.value === item.value)) {
          return {
            ...block,
            prompts: block.prompts.map((p) =>
              p.value === item.value ? { ...p, ...configData } : p,
            ),
          };
        }

        if (block.events.find((e) => e.value === item.value)) {
          return {
            ...block,
            events: block.events.map((e) =>
              e.value === item.value ? { ...e, ...configData } : e,
            ),
          };
        }

        if (block.libraries.find((l) => l.value === item.value)) {
          return {
            ...block,
            libraries: block.libraries.map((l) =>
              l.value === item.value ? { ...l, ...configData } : l,
            ),
          };
        }

        return block;
      }),
    );

    toast.success("Configurações salvas com sucesso!");
  };

  const addBlockToList = () => {
    if (!blockTitle.trim()) {
      toast.error("Informe o título do bloco");
      return;
    }

    if (!blockScore || isNaN(blockScore)) {
      toast.error("Informe um peso válido para o bloco");
      return;
    }

    const blockId = getCurrentBlockId();
    const blockConfigurations = {};

    selectedBlockPrompts.forEach((prompt) => {
      const key = getItemKey(prompt, "prompt", blockId);
      if (itemConfigurations[key]) {
        blockConfigurations[`prompt_${prompt.value}`] = itemConfigurations[key];
      }
    });

    selectedBlockEvents.forEach((event) => {
      const key = getItemKey(event, "event", blockId);
      if (itemConfigurations[key]) {
        blockConfigurations[`event_${event.value}`] = itemConfigurations[key];
      }
    });

    selectedBlockLibraries.forEach((library) => {
      const key = getItemKey(library, "library", blockId);
      if (itemConfigurations[key]) {
        blockConfigurations[`library_${library.value}`] =
          itemConfigurations[key];
      }
    });

    const newBlock = {
      id:
        editingBlockIndex !== null
          ? createdBlocks[editingBlockIndex].id
          : Date.now(),
      uniqueId: blockId,
      title: blockTitle.trim(),
      score: Number(blockScore),
      color: blockColor,
      priority: blockPriority,
      prompts: selectedBlockPrompts,
      events: selectedBlockEvents,
      libraries: selectedBlockLibraries,
      totalItems: getTotalSelectedItems(),
      configurations: blockConfigurations,
    };

    if (editingBlockIndex !== null) {
      const updatedBlocks = [...createdBlocks];
      updatedBlocks[editingBlockIndex] = newBlock;
      setCreatedBlocks(updatedBlocks);
      setEditingBlockIndex(null);
    } else {
      setCreatedBlocks([...createdBlocks, newBlock]);
      clearNewBlockConfigurations();
    }

    setShowBlockForm(false);
    setSelectedBlockPrompts([]);
    setSelectedBlockEvents([]);
    setSelectedBlockLibraries([]);
    setBlockTitle("");
    setBlockScore("");
    setBlockColor("#4A90E2");
    setBlockPriority(false);
    setCurrentBlockId(null);
  };

  const editBlock = (index) => {
    const block = createdBlocks[index];
    setSelectedBlockPrompts(block.prompts);
    setSelectedBlockEvents(block.events);
    setSelectedBlockLibraries(block.libraries);
    setBlockTitle(block.title);
    setBlockScore(block.score.toString());
    setBlockColor(block.color);
    setBlockPriority(block.priority);
    setEditingBlockIndex(index);

    setItemConfigurations({});

    if (block.configurations) {
      const blockConfigurations = {};

      block.prompts.forEach((prompt) => {
        const savedConfig = block.configurations[`prompt_${prompt.value}`];
        if (savedConfig) {
          const key = getItemKey(prompt, "prompt", index);
          blockConfigurations[key] = savedConfig;
        }
      });

      block.events.forEach((event) => {
        const savedConfig = block.configurations[`event_${event.value}`];
        if (savedConfig) {
          const key = getItemKey(event, "event", index);
          blockConfigurations[key] = savedConfig;
        }
      });

      block.libraries.forEach((library) => {
        const savedConfig = block.configurations[`library_${library.value}`];
        if (savedConfig) {
          const key = getItemKey(library, "library", index);
          blockConfigurations[key] = savedConfig;
        }
      });

      setItemConfigurations(blockConfigurations);
    }

    setShowBlockForm(true);
  };

  const removeBlock = (index) => {
    const updatedBlocks = createdBlocks.filter((_, i) => i !== index);
    setCreatedBlocks(updatedBlocks);
  };

  const cancelBlockForm = () => {
    if (editingBlockIndex === null) {
      const currentBlockId = "new";
      setItemConfigurations((prev) => {
        const newConfig = { ...prev };
        Object.keys(newConfig).forEach((key) => {
          if (key.startsWith(`${currentBlockId}_`)) {
            delete newConfig[key];
          }
        });
        return newConfig;
      });
    }

    setShowBlockForm(false);
    setSelectedBlockPrompts([]);
    setSelectedBlockEvents([]);
    setSelectedBlockLibraries([]);
    setBlockTitle("");
    setBlockScore("");
    setBlockColor("#4A90E2");
    setBlockPriority(false);
    setEditingBlockIndex(null);
  };

  const clearNewBlockConfigurations = () => {
    setItemConfigurations((prev) => {
      const newConfig = { ...prev };
      Object.keys(newConfig).forEach((key) => {
        if (key.startsWith("new_")) {
          delete newConfig[key];
        }
      });
      return newConfig;
    });
  };

  return (
    <div className="pl-lg-12">
      <Col className="pl-lg-12">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Configuração de Blocos</h4>
          <Button
            color="primary"
            size="sm"
            onClick={() => {
              setItemConfigurations({});
              setCurrentBlockId(generateUniqueBlockId());
              setShowBlockForm(true);
            }}
          >
            <i className="ni ni-fat-add"></i> Criar novo bloco
          </Button>
        </div>

        {showBlockForm && (
          <Card className="mb-4">
            <CardBody>
              <h5>Novo Bloco de Regras</h5>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <label className="form-control-label">
                      Título do Bloco
                    </label>
                    <input
                      className="form-control"
                      placeholder="Ex: Abertura do Atendimento"
                      type="text"
                      value={blockTitle}
                      onChange={(evt) => setBlockTitle(evt.target.value)}
                      autoComplete="off"
                    />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <label className="form-control-label">Peso do Bloco</label>
                    <input
                      className="form-control"
                      placeholder="Ex: 50"
                      type="number"
                      min="1"
                      value={blockScore}
                      onChange={(evt) => setBlockScore(evt.target.value)}
                      autoComplete="off"
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="4">
                  <FormGroup>
                    <label className="form-control-label">Prompts</label>
                    <Select
                      isMulti
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="Selecione prompts"
                      value={[]}
                      onChange={(selectedOptions) => {
                        if (selectedOptions && selectedOptions.length > 0) {
                          const newItems = selectedOptions.filter(
                            (option) =>
                              !selectedBlockPrompts.find(
                                (existing) => existing.value === option.value,
                              ),
                          );
                          if (newItems.length > 0) {
                            setSelectedBlockPrompts([
                              ...selectedBlockPrompts,
                              ...newItems,
                            ]);
                          }
                        }
                      }}
                      options={availablePrompts.filter(
                        (prompt) =>
                          !selectedBlockPrompts.find(
                            (selected) => selected.value === prompt.value,
                          ),
                      )}
                      isClearable={false}
                      isSearchable={true}
                    />
                    {selectedBlockPrompts.length > 0 && (
                      <div className="mt-2">
                        <small className="text-muted d-block mb-1">
                          Clique em um prompt para configurá-lo:
                        </small>
                        <div style={{ display: "flex", flexWrap: "wrap" }}>
                          {selectedBlockPrompts.map((prompt) => (
                            <ClickableTag
                              key={prompt.value}
                              data={prompt}
                              type="prompt"
                              onRemove={(item) =>
                                handleRemoveItem(item, "prompt")
                              }
                              onConfigure={handleConfigureItem}
                              isConfigured={isItemConfigured(prompt, "prompt")}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md="4">
                  <FormGroup>
                    <label className="form-control-label">
                      Eventos Avaliáveis
                    </label>
                    <Select
                      isMulti
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="Selecione eventos"
                      value={[]}
                      onChange={(selectedOptions) => {
                        if (selectedOptions && selectedOptions.length > 0) {
                          const newItems = selectedOptions.filter(
                            (option) =>
                              !selectedBlockEvents.find(
                                (existing) => existing.value === option.value,
                              ),
                          );
                          if (newItems.length > 0) {
                            setSelectedBlockEvents([
                              ...selectedBlockEvents,
                              ...newItems,
                            ]);
                          }
                        }
                      }}
                      options={evaluableEvents.filter(
                        (event) =>
                          !selectedBlockEvents.find(
                            (selected) => selected.value === event.value,
                          ),
                      )}
                      isClearable={false}
                      isSearchable={true}
                    />
                    {selectedBlockEvents.length > 0 && (
                      <div className="mt-2">
                        <small className="text-muted d-block mb-1">
                          Clique em um evento para configurá-lo:
                        </small>
                        <div style={{ display: "flex", flexWrap: "wrap" }}>
                          {selectedBlockEvents.map((event) => (
                            <ClickableTag
                              key={event.value}
                              data={event}
                              type="event"
                              onRemove={(item) =>
                                handleRemoveItem(item, "event")
                              }
                              onConfigure={handleConfigureItem}
                              isConfigured={isItemConfigured(event, "event")}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md="4">
                  <FormGroup>
                    <label className="form-control-label">Bibliotecas</label>
                    <Select
                      isMulti
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="Selecione bibliotecas"
                      value={[]}
                      onChange={(selectedOptions) => {
                        if (selectedOptions && selectedOptions.length > 0) {
                          const newItems = selectedOptions.filter(
                            (option) =>
                              !selectedBlockLibraries.find(
                                (existing) => existing.value === option.value,
                              ),
                          );
                          if (newItems.length > 0) {
                            setSelectedBlockLibraries([
                              ...selectedBlockLibraries,
                              ...newItems,
                            ]);
                          }
                        }
                      }}
                      options={libraries
                        .map((lib) => ({
                          label: lib.title,
                          value: lib.id,
                          terms: lib.terms,
                        }))
                        .filter(
                          (library) =>
                            !selectedBlockLibraries.find(
                              (selected) => selected.value === library.value,
                            ),
                        )}
                      isClearable={false}
                      isSearchable={true}
                    />
                    {selectedBlockLibraries.length > 0 && (
                      <div className="mt-2">
                        <small className="text-muted d-block mb-1">
                          Clique em uma biblioteca para configurá-la:
                        </small>
                        <div style={{ display: "flex", flexWrap: "wrap" }}>
                          {selectedBlockLibraries.map((library) => (
                            <ClickableTag
                              key={library.value}
                              data={library}
                              type="library"
                              onRemove={(item) =>
                                handleRemoveItem(item, "library")
                              }
                              onConfigure={handleConfigureItem}
                              isConfigured={isItemConfigured(
                                library,
                                "library",
                              )}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup>
                    <label className="form-control-label">Cor do Bloco</label>
                    <div className="mt-2">
                      <div className="d-flex flex-wrap">
                        {colorPalette.map((color, index) => (
                          <div
                            key={index}
                            className={`color-option ${
                              blockColor === color ? "selected" : ""
                            }`}
                            style={{
                              backgroundColor: color,
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              margin: "5px",
                              cursor: "pointer",
                              border:
                                blockColor === color
                                  ? "3px solid #000"
                                  : "2px solid #ddd",
                              transition: "all 0.2s ease",
                            }}
                            onClick={() => setBlockColor(color)}
                            title={color}
                          />
                        ))}
                      </div>
                      <small className="text-muted mt-2 d-block">
                        Cor selecionada: {blockColor}
                      </small>
                    </div>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="12">
                  <FormGroup check className="p-0">
                    <label className="form-control-label">
                      <input
                        type="checkbox"
                        checked={blockPriority}
                        onChange={(e) => setBlockPriority(e.target.checked)}
                      />{" "}
                      Prioridade Absoluta
                    </label>
                  </FormGroup>
                </Col>
              </Row>

              <div className="mt-3 pb-3 mb-3 pt-3 border-top border-bottom">
                <div className="d-flex align-items-center">
                  <i
                    className={`ni ${areAllItemsConfigured() ? "ni-check-bold text-success" : "ni-time-alarm text-warning"}`}
                    style={{ marginRight: "8px", fontSize: "1.2rem" }}
                  ></i>
                  <span
                    className={`font-weight-bold ${areAllItemsConfigured() ? "text-success" : "text-warning"}`}
                  >
                    {areAllItemsConfigured()
                      ? "Todas as configurações concluídas! ✓"
                      : `${getUnconfiguredItemsDetails().total} item(ns) pendente(s) de configuração`}
                  </span>
                </div>
              </div>

              <div className="d-flex justify-content-between mt-3">
                <Button color="secondary" size="sm" onClick={cancelBlockForm}>
                  Cancelar
                </Button>

                <Button
                  color="success"
                  size="sm"
                  disabled={
                    getTotalSelectedItems() < 1 || !areAllItemsConfigured()
                  }
                  onClick={addBlockToList}
                >
                  {getValidationMessage()}
                </Button>
              </div>
            </CardBody>
          </Card>
        )}

        {createdBlocks.length > 0 && (
          <div className="mt-4">
            <h5>Blocos Configurados ({createdBlocks.length})</h5>
            {createdBlocks.map((block, index) => (
              <Card key={block.id} className="mb-3">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <div
                            style={{
                              backgroundColor: block.color,
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              marginRight: "10px",
                              border: "2px solid #ddd",
                            }}
                          />
                          <h3 className="mb-0">{block.title}</h3>
                        </div>
                      </div>
                      <div className="text-sm text-muted">
                        <div className="mb-1">
                          <strong>Peso:</strong> {block.score}
                        </div>
                        <div className="mb-1">
                          <strong>Prioridade:</strong>{" "}
                          {block.priority ? "Sim" : "Não"}
                        </div>
                        <div className="mb-1">
                          <strong>Prompts:</strong>{" "}
                          {block.prompts.length > 0
                            ? block.prompts.map((p) => p.label).join(", ")
                            : "Nenhum"}
                        </div>
                        <div className="mb-1">
                          <strong>Eventos:</strong>{" "}
                          {block.events.length > 0
                            ? block.events.map((e) => e.label).join(", ")
                            : "Nenhum"}
                        </div>
                        <div className="mb-1">
                          <strong>Bibliotecas:</strong>{" "}
                          {block.libraries.length > 0
                            ? block.libraries.map((l) => l.label).join(", ")
                            : "Nenhuma"}
                        </div>
                        <div>
                          <strong>Total de itens:</strong> {block.totalItems}
                        </div>
                      </div>
                    </div>
                    <div
                      className="d-flex flex-column ml-3 align-items-baseline"
                      style={{ minWidth: "80px" }}
                    >
                      <Button
                        color="info"
                        size="sm"
                        className="mb-2 d-flex align-items-center justify-content-center"
                        onClick={() => editBlock(index)}
                        style={{
                          height: "36px",
                          width: "36px",
                          padding: "0",
                          fontSize: "0.875rem",
                        }}
                        title="Editar bloco"
                      >
                        <i className="ni ni-settings"></i>
                      </Button>

                      <Button
                        color="danger"
                        size="sm"
                        className="d-flex align-items-center justify-content-center"
                        onClick={() => removeBlock(index)}
                        style={{
                          height: "36px",
                          width: "36px",
                          padding: "0",
                          fontSize: "0.875rem",
                        }}
                        title="Remover bloco"
                      >
                        <i className="ni ni-fat-remove"></i>
                      </Button>
                    </div>
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        )}

        {showConfigModal && currentConfigItem && (
          <ConfigurationModal
            isOpen={showConfigModal}
            onClose={() => setShowConfigModal(false)}
            onSave={saveItemConfiguration}
            configuredRules={configuredRules}
            setConfiguredRules={setConfiguredRules}
            item={currentConfigItem}
            type={currentConfigType}
            currentBlockId={getCurrentBlockId()}
          />
        )}
      </Col>
    </div>
  );
};

export default BlockConfiguration;
